import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  GetVibeCostSafetyResponseSchema,
  GetVibeTemplatePathVarsSchema,
  GetVibeTemplateResponseSchema,
  GetVibeUsageCycleResponseSchema,
  GetVibeUsageQuerySchema,
  GetVibeUsageResponseSchema,
  ListPublishedVibeTemplatesResponseSchema,
  ListVibeAuditEventsQuerySchema,
  ListVibeAuditEventsResponseSchema
} from "../schemas";

export const VibeCatalogEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeTemplate — curated starter project catalog (global, read-only at v1)
  // ---------------------------------------------------------------------------

  /**
   * List published Vibe templates. The catalog is GLOBAL (not org-
   * scoped) — Nexus curates the contents, customers consume them.
   * Unpublished rows never appear here; the admin curation surface
   * lives in a separate later slice. Endpoint is still gated by
   * `FeatureFlag.VIBE` because the CLI flow is only meaningful for
   * orgs that have Vibe enabled.
   */
  ListTemplates: {
    method: "GET" as const,
    path: "/api/vibe/templates",
    Response: ApiSuccessSchema(ListPublishedVibeTemplatesResponseSchema)
  },

  /**
   * Fetch one template by slug. 404 if the slug is unknown OR if the
   * row is unpublished — the public read does not leak the existence
   * of staging-content templates. Same cross-tenant-style "missing
   * is missing" stance applied to a non-tenant resource.
   */
  GetTemplate: {
    method: "GET" as const,
    path: "/api/vibe/templates/:slug",
    PathVars: GetVibeTemplatePathVarsSchema,
    Response: ApiSuccessSchema(GetVibeTemplateResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeAuditEvent — append-only audit feed
  // ---------------------------------------------------------------------------

  /**
   * Read the org's audit feed. Always org-scoped (the controller
   * injects `@CurrentOrganizationId()`); the optional `vibeAppId`
   * query narrows further to a single app's events for the "what
   * happened on this app?" view, and the optional `eventType` narrows
   * by row class so the reviewer-dashboard can pre-filter to
   * `DEPLOYMENT_APPROVED` / `DEPLOYMENT_REJECTED` etc. without
   * client-side post-filter.
   *
   * Cursor-paginated because the table is append-only + monotonically
   * increasing — offset pagination would degrade quadratically against
   * the growing tail.
   */
  ListAuditEvents: {
    method: "GET" as const,
    path: "/api/vibe/audit-events",
    Query: ListVibeAuditEventsQuerySchema,
    Response: ApiSuccessSchema(ListVibeAuditEventsResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // Usage dashboard (L6)
  // ---------------------------------------------------------------------------

  /**
   * Org-scoped usage read over the daily `VibeUsageRollup` series. The rollup
   * grain has no per-app dimension, so usage is always whole-org: one response
   * carries every usage type's window total + daily points. Quantities only —
   * no rated $ at v1.
   */
  GetUsage: {
    method: "GET" as const,
    path: "/api/vibe/usage",
    Query: GetVibeUsageQuerySchema,
    Response: ApiSuccessSchema(GetVibeUsageResponseSchema)
  },

  /**
   * Org-scoped billing-cycle usage read: per usage type, the cycle-to-date
   * total and its daily series against the effective cap, plus per-app
   * attribution. Summed from `VibeUsageEvent` over the current UTC calendar
   * month — the same table and window `GetCostSafety` meters against, so the
   * cumulative burn curve and the tile meter cannot disagree.
   *
   * Distinct from `GetUsage`, which reads the daily `VibeUsageRollup` over a
   * trailing 30-day window. Both are correct; they answer different questions
   * over different windows, and the console names each one.
   */
  GetUsageCycle: {
    method: "GET" as const,
    path: "/api/vibe/usage/cycle",
    Response: ApiSuccessSchema(GetVibeUsageCycleResponseSchema)
  },

  /**
   * Org-scoped cost-safety read — the self-serve counterpart to the admin-only
   * cost-safety + consumption-cap surfaces. Returns the caller org's current
   * cost-safety `status` (OK / WARNING / SUSPENDED) plus, per usage type, the
   * current UTC-month consumption against its effective cap. The console draws
   * the usage-vs-cap meters and the soft-limit WARNING banner from this.
   */
  GetCostSafety: {
    method: "GET" as const,
    path: "/api/vibe/cost-safety",
    Response: ApiSuccessSchema(GetVibeCostSafetyResponseSchema)
  }
} as const;
